<template>
  <DsModal
    :open="true"
    :full-screen="true"
    :title="$t('components.ComponentBuildModal.label.title')"
    :link-label="$t('components.ComponentBuildModal.label.button.clearAll')"
    :primary-button-label="$t('components.ComponentBuildModal.label.button.apply')"
    :secondary-button-label="$t('components.ComponentBuildModal.label.button.cancel')"
    @link="clearAction()"
    @secondary="cancelAction()"
    @primary="applyAction()"
  >
    <template slot="content">
      <section class="component-build-modal">
        <article class="component-build-modal__form">
          <v-form
            ref="componentBuildForm"
            v-model="formValue"
          >
            <article class="component-build-modal__data">
              <header class="component-build-modal__title">
                {{ $t('components.ComponentBuildModal.label.data.title') }}
              </header>

              <v-row
                no-gutters
                class="justify-space-between"
              >
                <v-col
                  v-for="(filter, index) in dataFilter"
                  :key="index"
                  cols="12"
                  md="6"
                  class="component-build-modal__input"
                >
                  <v-row
                    no-gutters
                  >
                    <component
                      :is="filter.component"
                      v-model="tempFilter[filter.props.filterType][filter.props.filter]"
                      v-bind="filter.props"
                    />
                  </v-row>
                </v-col>
              </v-row>
            </article>

            <article class="component-build-modal__sensitive-data">
              <DsSwitch
                v-model="tempFilter.areSensitiveData"
                :label="$t('components.ComponentBuildModal.label.sensitiveData.label')"
              />
            </article>

            <article class="component-build-modal__details">
              <header class="component-build-modal__title">
                {{ $t('components.ComponentBuildModal.label.details.title') }}
              </header>

              <v-row no-gutters>
                <v-col>
                  <v-row
                    class="component-build-modal__field"
                    no-gutters
                  >
                    <ComponentTypeFilter
                      v-model="tempFilter.details.componentType"
                      v-bind="detailsFilter.componentType.props"
                    />
                  </v-row>

                  <v-row
                    class="component-build-modal__field"
                    no-gutters
                  >
                    <v-text-field
                      v-model="tempFilter.details.title"
                      v-bind="detailsFilter.title.props"
                    />
                  </v-row>

                  <v-row
                    class="component-build-modal__description"
                    no-gutters
                  >
                    <v-textarea
                      v-model="tempFilter.details.description"
                      v-bind="detailsFilter.description.props"
                    />
                  </v-row>

                  <v-row no-gutters>
                    <v-col class="d-flex justify-space-between">
                      <v-row
                        no-gutters
                        class="component-build-modal__input"
                      >
                        <WidthFilter
                          v-model="tempFilter.details.width"
                          v-bind="detailsFilter.width.props"
                        />
                      </v-row>

                      <v-row
                        no-gutters
                        class="component-build-modal__input"
                      >
                        <HeightFilter
                          v-model="tempFilter.details.height"
                          v-bind="detailsFilter.height.props"
                        />
                      </v-row>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </article>

            <article class="component-build-modal__viewControl">
              <header class="component-build-modal__title">
                {{ $t('components.ComponentBuildModal.label.viewControl.title') }}
              </header>

              <v-text-field
                v-model="tempFilter.featureFlag"
                v-bind="viewControl.featureFlag.props"
              />
            </article>
          </v-form>
        </article>
      </section>
    </template>
  </DsModal>
</template>

<script>
import { MUTATE } from '@/store/modules/mutation_types';
import {
  mapMutations,
  mapActions,
} from 'vuex';

import SingleSelect from '@/components/filter/models/SingleSelect';
import OriginFilter from '@/components/filter/OriginFilter';
import FieldFilter from '@/components/filter/FieldFilter';
import OperationFilter from '@/components/filter/OperationFilter';
import SortingFilter from '@/components/filter/SortingFilter';
import ComponentTypeFilter from '@/components/filter/ComponentTypeFilter';
import WidthFilter from '@/components/filter/WidthFilter';
import HeightFilter from '@/components/filter/HeightFilter';
import {
  DsModal,
  DsSwitch,
} from '@ds/components';

import { trackEventWithUserProperties } from '@/utils/mixpanel';

import componentBuildModel from '@/mixins/component-build-model';

export default {
  name: 'ComponentBuildModal',
  components: {
    DsModal,
    DsSwitch,
    SingleSelect,
    OriginFilter,
    FieldFilter,
    OperationFilter,
    SortingFilter,
    ComponentTypeFilter,
    WidthFilter,
    HeightFilter,
  },
  mixins: [
    componentBuildModel,
  ],
  computed: {
    dataFilter() {
      return [{
        component: 'OriginFilter',
        props: {
          filterType: 'data',
          filter: 'origin',
          rules: this.rules.default,
        },
      },
      {
        component: 'FieldFilter',
        props: {
          filterType: 'data',
          filter: 'field',
          rules: this.rules.default,
          label: this.$t('components.filters.FieldFilter.label.field'),
          items: this.fieldItems,
        },
      },
      {
        component: 'OperationFilter',
        props: {
          filterType: 'data',
          filter: 'operation',
          rules: this.rules.default,
          currentField: this.tempFilter.data.field,
        },
      },
      {
        component: 'FieldFilter',
        props: {
          filterType: 'data',
          filter: 'grouping',
          rules: this.rules.default,
          label: this.$t('components.filters.FieldFilter.label.groupingBy'),
          items: this.groupingItems,
        },
      },
      {
        component: 'SortingFilter',
        props: {
          filterType: 'data',
          filter: 'sorting',
          rules: this.rules.default,
        },
      }];
    },
    detailsFilter() {
      return {
        componentType: {
          props: {
            rules: this.rules.default,
          },
        },
        title: {
          props: {
            label: this.$t('components.ComponentBuildModal.label.details.titleInput'),
            rules: this.rules.detailsFilter.title,
            counter: 50,
            maxlength: 50,
            outlined: true,
            dense: true,
          },
        },
        description: {
          props: {
            label: this.$t('components.ComponentBuildModal.label.details.descriptionInput'),
            rules: this.rules.detailsFilter.description,
            rows: 2,
            counter: 200,
            maxlength: 200,
            noResize: true,
            outlined: true,
            dense: true,
          },
        },
        width: {
          props: {
            rules: this.rules.default,
          },
        },
        height: {
          props: {
            rules: this.rules.default,
          },
        },
      };
    },
    viewControl() {
      return {
        featureFlag: {
          props: {
            label: this.$t('components.ComponentBuildModal.label.viewControl.featureFlag'),
            hideDetails: 'auto',
            rules: this.rules.viewControl.featureFlag,
            dense: true,
            outlined: true,
          },
        },
      };
    },
  },
  watch: {
    tempFilter: {
      handler(newValue) {
        const isValidadeObject = this.validateObjectValues(newValue);

        if (isValidadeObject) {
          this.changeDisabledValueOfApplyButton(false);
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.changeDisabledValueOfApplyButton(true);
  },
  methods: {
    ...mapActions('dashboard', [
      'saveNewCustomCard',
    ]),
    ...mapMutations('misc', {
      mutateMisc: MUTATE,
    }),
    closeComponentBuildModal() {
      this.mutateMisc({
        property: 'showComponentBuildModal',
        with: false,
      });
    },
    clearAction() {
      this.tempFilter = this.getDefaultFilter();
      this.$refs.componentBuildForm.resetValidation();
    },
    cancelAction() {
      this.closeComponentBuildModal();
    },
    async applyAction() {
      const isFormValid = await this.$refs.componentBuildForm.validate();

      if (isFormValid) {
        const newComponent = this.transformFormDataIntoComponentObject();

        await this.saveNewCustomCard(newComponent);

        this.$noty.success(this.$t('components.ComponentBuildModal.message.createComponent'), { layout: 'bottomCenter' });
        trackEventWithUserProperties('ST ComponentBuilder Component Add');

        this.mutateMisc({
          property: 'showComponentBuildModal',
          with: false,
        });
      }
    },
  },
};
</script>

<style scoped lang="scss">
.component-build-modal {
  $input-size: 224px;
  $form-width: 464px;

  font-family: var(--ds-font-family);

  &__form {
    width: $form-width;
  }

  &__title {
    font-style: normal;
    font-weight: var(--ds-font-weight-semibold);
    font-size: var(--ds-font-size-md);
    line-height: 24px;
    margin-bottom: var(--ds-spacing-stack-xs);
  }

  &__input {
    max-height: 40px;
    max-width: $input-size;
    line-height: 24px;
    height: var(--ds-spacing-stack-lg);
    margin-bottom: var(--ds-spacing-stack-md);
  }

  &__field {
    max-height: 56px;
    margin-bottom: var(--ds-spacing-stack-xs) !important;
  }

  &__description {
    max-height: 104px;
    margin-bottom: var(--ds-spacing-stack-xs) !important;
  }

  &__sensitive-data {
    margin-bottom: 48px;
  }
}

div[role="listbox"] {
  background-color: red !important;
}

div[role="listbox"].v-list.v-select-list.v-sheet:first-child {
  border-bottom: 2px solid red !important;
}

::v-deep {
  .ds-modal {
    &__component {
      padding: 0 calc((100vw - 1196px) / 2);
      background-color: var(--ds-color-surface-contrast);
    }

    &__content {
      margin-top: 0;
    }

    &__header {
      padding-bottom: var(--ds-spacing-stack-sm);
      line-height: 48px;
    }

    &__container {
      padding-top: 0;
      padding-bottom: 0;
    }

    &__slot-content {
      margin-top: var(--ds-spacing-stack-sm);
    }

    &__title {
      color: var(--ds-color-text-1);
      font-weight: var(--ds-font-weight-semibold);
      font-size: var(--ds-font-size-xl);
      line-height: var(--ds-font-line-height-normal);
    }
  }

  label.v-label {
    left: var(--ds-spacing-inline-nano) !important;
  }

  textarea,
  input,
  .v-select__selection.v-select__selection--comma {
    padding-left: var(--ds-spacing-inline-nano);
  }

  .v-text-field__details {
    display: flex !important;
  }

  div[role="listbox"] {
    background-color: red !important;
  }

  div[role="listbox"].v-list.v-select-list.v-sheet:first-child {
    border-bottom: 2px solid red !important;
  }
}
</style>
